import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Search, MapPin, Navigation as NavIcon, Radio, Send, FileText, Wallet, Caravan, QrCode, AlertOctagon, MessageSquare, ArrowRight, Smartphone } from "lucide-react";
import { SectionHeading } from "@/components/shared/SectionHeading";
import { Reveal } from "@/components/shared/Reveal";
import { openSignIn } from "@/lib/signInModal";

const CAPS = [
  { icon: Search, label: "Find Jobs" }, { icon: MapPin, label: "Map" }, { icon: NavIcon, label: "Active Assignment" },
  { icon: NavIcon, label: "Navigation" }, { icon: Radio, label: "ELD/HOS" }, { icon: Send, label: "Relief Request" },
  { icon: FileText, label: "Handoff" }, { icon: FileText, label: "Documents" }, { icon: Wallet, label: "Wallet" },
  { icon: Wallet, label: "Payments" }, { icon: Caravan, label: "RV Jobs" }, { icon: QrCode, label: "Inspection Mode" },
  { icon: AlertOctagon, label: "Emergency Reporting" }, { icon: MessageSquare, label: "Messages" },
];

const SCREENS = [
  { src: "/assets/app-screens/screen-intro.jpg", label: "Keep freight moving" },
  { src: "/assets/app-screens/screen-trusted-drivers.jpg", label: "Trusted drivers, transparent pay" },
  { src: "/assets/app-screens/screen-rv-rescue.jpg", label: "RV rescue & delivery, tracked live" },
  { src: "/assets/app-screens/screen-inspection.jpg", label: "DOT inspection mode" },
];

export const MobileApp = () => {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setActive((a) => (a + 1) % SCREENS.length), 3200);
    return () => clearInterval(t);
  }, [active]);

  const handleApkDownload = () => toast.info("The RelayRigs Android app is coming soon — join early access and we'll notify you the moment it launches.");

  return (
    <section data-testid="mobile-app-section" className="py-24 md:py-32 bg-rr-navy relative overflow-hidden">
      <div className="grain-overlay" />
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-[1fr_0.8fr] gap-16 items-center">
        <div>
          <SectionHeading eyebrow="Mobile App" title="RelayRigs Goes Where the Driver Goes." light testId="mobile-app" />
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-2.5">
            {CAPS.map((c) => (
              <div key={c.label} data-testid={`app-capability-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="flex items-center gap-2 bg-white/[0.06] border border-white/10 rounded-lg px-3 py-2.5 text-xs font-semibold text-white/80">
                <c.icon size={14} className="text-rr-orangeLight shrink-0" /> {c.label}
              </div>
            ))}
          </div>
          <Reveal delay={0.3}>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <button onClick={() => openSignIn("register")} data-testid="join-early-access-app-cta" className="inline-flex items-center gap-2 bg-rr-orange text-white font-bold px-7 py-4 rounded-full hover:bg-white hover:text-rr-navy transition-all hover:-translate-y-1">
                Create Your Account <ArrowRight size={18} />
              </button>
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <button
                onClick={handleApkDownload}
                data-testid="google-play-badge-button"
                aria-label="Google Play — coming soon"
                className="relative group opacity-60 hover:opacity-90 transition-opacity cursor-default"
              >
                <img
                  src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                  alt="Get it on Google Play"
                  className="h-12 grayscale group-hover:grayscale-0 transition-all"
                />
                <span className="absolute -top-2 -right-2 bg-rr-orange text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full leading-none">SOON</span>
              </button>
              <button
                onClick={handleApkDownload}
                data-testid="app-store-badge-button"
                aria-label="App Store — coming soon"
                className="relative group opacity-60 hover:opacity-90 transition-opacity cursor-default"
              >
                <img
                  src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                  alt="Download on the App Store"
                  className="h-12 grayscale group-hover:grayscale-0 transition-all"
                />
                <span className="absolute -top-2 -right-2 bg-rr-orange text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full leading-none">SOON</span>
              </button>
            </div>
            <p className="mt-3 text-xs text-white/40 flex items-center gap-1.5">
              <Smartphone size={13} /> Google Play &amp; App Store listings are in review — badges will go live once approved.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          <div className="relative mx-auto w-64">
            <div className="rounded-[2.2rem] border-[6px] border-white/10 bg-black shadow-2xl overflow-hidden aspect-[9/18.5] relative">
              <AnimatePresence mode="wait">
                <motion.img
                  key={active}
                  src={SCREENS[active].src}
                  alt={SCREENS[active].label}
                  initial={{ opacity: 0, scale: 1.02 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                  className="absolute inset-0 w-full h-full object-cover"
                  data-testid="app-screenshot-preview"
                />
              </AnimatePresence>
            </div>

            <div className="flex items-center justify-center gap-2 mt-6">
              {SCREENS.map((s, i) => (
                <button
                  key={s.label}
                  onClick={() => setActive(i)}
                  data-testid={`app-screen-dot-${i}`}
                  aria-label={s.label}
                  className={`h-1.5 rounded-full transition-all ${i === active ? "w-8 bg-rr-orange" : "w-3 bg-white/20"}`}
                />
              ))}
            </div>
            <p className="text-center text-xs text-white/50 mt-3 min-h-[16px]">{SCREENS[active].label}</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
